import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  getJsonLdOrganizationAndWebsite,
  getSeoForPath,
  SITE_NAME,
} from '../../config/siteSeo';

/**
 * Route-aware defaults: canonical, Open Graph, Twitter, JSON-LD.
 * Pages that define their own `<Helmet>` still override matching tags when mounted later.
 */
const GlobalRouteSeo: React.FC = () => {
  const { pathname } = useLocation();
  const canonical = absoluteUrl(pathname);
  const seo = useMemo(() => getSeoForPath(pathname), [pathname]);
  const structured = useMemo(() => JSON.stringify(getJsonLdOrganizationAndWebsite()), []);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <link rel="canonical" href={canonical} />

      {seo ? (
        <>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          {seo.robots ? <meta name="robots" content={seo.robots} /> : <meta name="robots" content="index, follow" />}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={SITE_NAME} />
          <meta property="og:locale" content="en_IN" />
          <meta property="og:url" content={canonical} />
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </>
      ) : (
        <>
          <meta name="robots" content="index, follow" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={SITE_NAME} />
          <meta property="og:locale" content="en_IN" />
          <meta property="og:url" content={canonical} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </>
      )}

      <script type="application/ld+json">{structured}</script>
    </Helmet>
  );
};

export default GlobalRouteSeo;
